import React from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { LayoutSection } from '../admin/components/builder/types';
import { bloggerService } from '../services/blogger';
import { db } from '../lib/firebase';
import { collection, query, getDocs } from 'firebase/firestore';
import { NewsCard } from './NewsCard';
import { WidgetPostList } from './WidgetPostList';
import { extractFirstImage, stripHtml, cn, formatBDDate } from '../lib/utils';
import { AdBanner } from './AdBanner';
import { FeaturedRowSkeleton } from './Loaders';
import { BreakingNewsTicker } from './BreakingNewsTicker';

const CompactPost = ({ post, imageVariant = "standard", isDarkTheme = false }: { post: any, imageVariant?: "standard" | "small" | "square", isDarkTheme?: boolean }) => {
  const imageUrl = extractFirstImage(post.content, 300, 168);
  
  let imageClass = "w-[85px] h-[58px] xl:w-[95px] xl:h-[65px]";
  let gapClass = "gap-3";
  if (imageVariant === "small") {
    imageClass = "w-[70px] h-[50px] xl:w-[80px] xl:h-[55px]";
    gapClass = "gap-1.5";
  } else if (imageVariant === "square") {
    imageClass = "w-[70px] h-[70px] xl:w-[80px] xl:h-[80px]";
  }

  return (
    <Link to={post.url ? new URL(post.url).pathname : `/post/${post.id}`} className="flex flex-col group">
      <div className={`flex ${gapClass} justify-between items-start mb-1.5`}>
         <h3 className={`font-bold text-[15px] xl:text-[16px] leading-[1.35] ${isDarkTheme ? 'text-white group-hover:text-[#D32F2F]' : 'text-[#111111] dark:text-gray-100 group-hover:text-[#D32F2F]'} transition-colors font-bengali flex-1 lg:mt-[-1px]`}>
           {post.title}
         </h3>
         {imageUrl && (
           <div className={`shrink-0 overflow-hidden relative bg-gray-100 dark:bg-gray-800 ml-1 ${imageClass}`}>
             <img src={imageUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
           </div>
         )}
      </div>
      <div className={`${isDarkTheme ? 'text-gray-300' : 'text-[#555555] dark:text-gray-400'} text-[13px] line-clamp-2 font-bengali leading-relaxed mb-2`}>
         {stripHtml(post.content).substring(0, 120)}...
      </div>
      <div className={`text-[11.5px] ${isDarkTheme ? 'text-gray-400' : 'text-[#888888] dark:text-gray-500'} font-bengali`}>
         {formatBDDate(post.published, true)}
      </div>
    </Link>
  );
};

function HeroSectionHero({ section, isDark }: { section: LayoutSection, isDark: boolean }) {
  const cat = section.config.category || 'Seba Hot News';
  
  const fetchHeroData = async () => {
    let featuredDbPosts: any[] = [];
    try {
        const snapshot = await getDocs(query(collection(db, 'featured_news')));
        snapshot.forEach(d => {
            featuredDbPosts.push({ id: d.id, ...d.data() });
        });
    } catch(e) {
        console.error("Failed to load featured news", e);
    }

    const res = await bloggerService.getPostsByLabel(cat, 6);
    let items = res.items || [];

    for (const f of featuredDbPosts) {
       if (!f.postId) continue;
       try {
           const bPost = await bloggerService.getPostById(f.postId);
           if (bPost) {
               // Remove it from current list if exists to avoid duplicates
               items = items.filter(p => p.id !== bPost.id);
               
               if (f.position === 'Hero Lead') {
                   items = [bPost, ...items];
               } else if (f.position === 'Featured Secondary') {
                   items = [items[0], bPost, ...items.slice(1)].filter(Boolean);
               } else {
                   items.push(bPost); // Or something
               }
           }
       } catch(e) {}
    }
    return { items: items.slice(0, 6) };
  };

  const { data: heroData, isLoading: heroLoading } = useSWR(`hero_with_pinned_${cat}`, fetchHeroData);
  const { data: latestData, isLoading: latestLoading } = useSWR('hero_latest_news', () => bloggerService.getPosts(undefined, 5));
  
  const posts = heroData?.items || [];
  const latestPosts = latestData?.items?.slice(0, 5) || [];

  const featuredBannerPost = posts[0];
  const leftPosts = posts.length > 2 ? posts.slice(1, 3) : [];
  const middleBottomPosts = posts.length > 4 ? posts.slice(3, 5) : [];

  const renderLatestNews = () => (
    <div className="w-full">
       <div className={`flex items-center justify-between mb-0 border-b ${isDark ? 'border-gray-800' : 'border-[#E5E5E5] dark:border-gray-800'} pb-0`}>
          <h2 className={`text-[16px] xl:text-[18px] font-bold font-bengali ${isDark ? 'text-white' : 'text-[#111111] dark:text-white'} flex items-center`}>
            <span className="w-1 h-3.5 xl:w-1.5 xl:h-4 bg-[#D32F2F] mr-2"></span>
            সর্বশেষ সংবাদ
          </h2>
          <Link 
            to={`/category/${encodeURIComponent('সব')}`}
            className="text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700 active:scale-95 transition-transform pb-1"
          >
            সব খবর
          </Link>
        </div>
        <div className="flex flex-col">
          {latestLoading ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className={`flex gap-3 py-1.5 min-h-[70px] xl:min-h-[76px] items-start ${i !== 4 ? `border-b ${isDark ? 'border-gray-800' : 'border-gray-100 dark:border-gray-800'}` : ''}`}>
                <div className="flex-1">
                  <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded animate-pulse mb-1 w-full"></div>
                  <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded animate-pulse mb-1 w-4/5"></div>
                  <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded animate-pulse w-3/5"></div>
                </div>
                <div className="w-[100px] xl:w-[110px] h-[56px] xl:h-[62px] bg-gray-100 dark:bg-gray-800 rounded animate-pulse shrink-0"></div>
              </div>
            ))
          ) : (
            <>
              {latestPosts.map((post, i) => (
                <Link key={post.id} to={post.url ? new URL(post.url).pathname : `/post/${post.id}`} className={`group flex gap-3 py-1.5 min-h-[70px] xl:min-h-[76px] items-start ${i !== latestPosts.length - 1 ? `border-b ${isDark ? 'border-gray-800' : 'border-gray-100 dark:border-gray-800'}` : ''}`}>
                  <div className="flex-1">
                    <h3 className={`text-[14px] xl:text-[13px] font-semibold font-bengali ${isDark ? 'text-gray-200 group-hover:text-[#D32F2F]' : 'text-[#111111] dark:text-gray-200 group-hover:text-[#D32F2F] dark:group-hover:text-[#D32F2F]'} leading-[1.4] line-clamp-3 mb-1 transition-colors`}>
                      {post.title}
                    </h3>
                  </div>
                  {extractFirstImage(post.content) && (
                    <div className="w-[100px] xl:w-[110px] h-[56px] xl:h-[62px] shrink-0 overflow-hidden rounded relative">
                      <img src={extractFirstImage(post.content)!} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" referrerPolicy="no-referrer" />
                    </div>
                  )}
                </Link>
              ))}
            </>
          )}
        </div>
    </div>
  );

  return (
    <div className={`mb-4 w-full ${section.config.bgColor || ''} ${section.config.padding || 'py-0'} rounded-xl`}>
      <div className="hidden md:block">
        {heroLoading && posts.length === 0 ? (
          <div className="flex w-full h-[460px] lg:h-[500px] gap-4">
             <div className="w-[250px] xl:w-[280px] h-full bg-gray-100 dark:bg-gray-800 rounded animate-pulse hidden lg:block"></div>
             <div className="flex-1 h-full bg-gray-100 dark:bg-gray-800 rounded animate-pulse"></div>
             <div className="w-[260px] lg:w-[280px] xl:w-[320px] h-full bg-gray-100 dark:bg-gray-800 rounded animate-pulse hidden md:block"></div>
          </div>
        ) : posts.length > 0 ? (
          <div className="flex flex-col md:grid md:grid-cols-[minmax(0,1fr)_260px] lg:grid-cols-[250px_minmax(0,1fr)_280px] xl:grid-cols-[280px_minmax(0,1fr)_320px] gap-6 lg:gap-3 xl:gap-4 items-start">
            
            {/* LEFT COL */}
            <div className={`order-2 lg:order-1 flex flex-col gap-6 w-full md:col-span-1 lg:border-r ${isDark ? 'border-gray-800' : 'border-[#E5E5E5] dark:border-gray-800'} lg:pr-3 xl:pr-4`}>
               {leftPosts.map((post, i) => (
                 <div key={post.id} className={cn(i !== leftPosts.length - 1 && `border-b ${isDark ? 'border-gray-800' : 'border-[#E5E5E5] dark:border-gray-800'} pb-6`)}>
                   <CompactPost post={post} imageVariant="small" isDarkTheme={isDark} />
                 </div>
               ))}
            </div>

            {/* MIDDLE COL */}
            <div className={`order-1 lg:order-2 w-full md:col-span-1 border-b md:border-b-0 md:border-r ${isDark ? 'border-gray-800' : 'border-[#E5E5E5] dark:border-gray-800'} pb-6 md:pb-0 md:pr-3 lg:pr-3 xl:pr-4`}>
              {featuredBannerPost && (
                <Link to={featuredBannerPost.url ? new URL(featuredBannerPost.url).pathname : `/post/${featuredBannerPost.id}`} className="group flex flex-col md:flex-col lg:flex-row gap-4 lg:gap-4 mb-6">
                  <div className="lg:w-[50%] shrink-0 overflow-hidden relative bg-gray-100 dark:bg-gray-800 order-1 lg:order-1">
                     <img src={extractFirstImage(featuredBannerPost.content, 1000, 562) || ''} alt="" className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500" loading="eager" fetchPriority="high" decoding="sync" />
                  </div>
                  <div className="lg:w-[50%] flex flex-col pt-1 order-2 lg:order-2">
                     <h2 className={`text-[18px] xl:text-[21px] font-bold leading-tight font-bengali ${isDark ? 'text-white' : 'text-[#111111] dark:text-white'} group-hover:text-[#D32F2F] transition-colors mb-2`}>
                       {featuredBannerPost.title}
                     </h2>
                     <div className={`text-[14px] xl:text-[15px] line-clamp-3 font-bengali leading-relaxed mb-3 ${isDark ? 'text-gray-300' : 'text-[#444444] dark:text-gray-300'}`}>
                       {stripHtml(featuredBannerPost.content).substring(0, 150)}...
                     </div>
                     <div className={`text-[12px] mt-auto font-bengali ${isDark ? 'text-gray-400' : 'text-[#666666]'}`}>
                        {formatBDDate(featuredBannerPost.published, true)}
                     </div>
                  </div>
                </Link>
              )}
              
              <div className={`border-t my-6 ${isDark ? 'border-gray-800' : 'border-[#E5E5E5] dark:border-gray-800'}`}></div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-3 xl:gap-4">
                {middleBottomPosts.map((post, i) => (
                  <div key={post.id} className={cn(i === 0 && `md:border-r md:pr-3 xl:pr-4 ${isDark ? 'border-gray-800' : 'border-[#E5E5E5] dark:border-gray-800'}`)}>
                    <CompactPost post={post} imageVariant="square" isDarkTheme={isDark} />
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT COL */}
            <div className="order-3 w-full md:col-span-1 md:row-span-2 lg:row-span-1 lg:sticky lg:top-[120px]">
               {renderLatestNews()}
            </div>
          </div>
        ) : null}
      </div>

      {/* Mobile hero */}
      <div className="mb-2 md:hidden">
        {heroLoading && posts.length === 0 ? <FeaturedRowSkeleton /> : posts.length > 0 ? (
          <div className="flex flex-col gap-4">
            {featuredBannerPost && (
              <NewsCard post={featuredBannerPost} featured={true} priority={true} isDarkTheme={isDark} />
            )}
            {posts.length > 1 && (
              <div className="-mx-4 px-4 overflow-hidden relative">
                <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2">
                   {posts.slice(1, 6).map(post => (
                     <div key={post.id} className="w-[180px] md:w-[220px] snap-center shrink-0">
                       <NewsCard post={post} variant="slider" isDarkTheme={isDark} />
                     </div>
                   ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
      <div className="mb-4 md:hidden px-0">
         {renderLatestNews()}
      </div>
    </div>
  );
}

export function DynamicSection({ section }: { section: LayoutSection }) {
  if (!section.isVisible) return null;

  const { config } = section;
  const isDark = config.bgColor === 'bg-gray-900' || config.bgColor === 'bg-indigo-900';

  if (section.type === 'ticker') {
     const tickerCat = config.category || '';
     const tickerLimit = config.tickerCount || 10;
     const fetcher = async () => {
         let bloggerPosts = [];
         if (tickerCat && tickerCat !== 'সব' && tickerCat !== 'Latest') {
             const res = await bloggerService.getPostsByLabel(tickerCat, tickerLimit);
             bloggerPosts = res.items || [];
         } else {
             const res = await bloggerService.getPosts(undefined, tickerLimit);
             bloggerPosts = res.items || [];
         }
         return bloggerPosts;
     };

     const { data: tickerPosts } = useSWR(`ticker_combined_${tickerCat}_${tickerLimit}`, fetcher);
     return (
       <div className="-mx-4 -mt-[10px] mb-[7px] relative z-10 w-[calc(100%+32px)]">
         <BreakingNewsTicker news={tickerPosts || []} speed={config.tickerSpeed || 45} delay={config.tickerDelay || 0} />
       </div>
     );
  }

  if (section.type === 'ad') {
    return (
      <div className={`mb-0 w-full ${config.bgColor || 'bg-transparent'} ${config.padding || 'py-0'}`}>
        {config.adCode ? (
          <AdBanner dataAdSlot={config.adCode.replace(/[^0-9]/g, '')} />
        ) : (
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg w-full h-[90px] flex items-center justify-center border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 font-mono text-xs text-center p-4">
             AdSense Placement ({section.title || 'Ad'})<br/>Configure Ad Slot ID in Builder Properties
          </div>
        )}
      </div>
    );
  }
  
  if (section.type === 'trending') {
    // For trending section, fetch recent posts and sort them by replies count to approximate popularity
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const startDate = oneWeekAgo.toISOString();

    const { data, isLoading } = useSWR(`trending_section_news_week_${config.category || 'all'}`, () => 
      config.category 
        ? bloggerService.getPostsByLabel(config.category, 20, undefined, startDate) 
        : bloggerService.getPosts(undefined, 20, startDate)
    );
    const limit = config.itemCount || 6;
    
    const posts = data?.items
       ? [...data.items].sort((a, b) => parseInt(b.replies?.totalItems || '0') - parseInt(a.replies?.totalItems || '0')).slice(0, limit)
       : [];

    return (
      <div className={`mb-0 w-full ${config.bgColor || ''} ${config.padding || 'py-[10px]'} rounded-xl`}>
         {config.showTitle !== false && (
           <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
             <h2 className={`text-lg font-bold font-bengali flex items-center ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
               <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{section.title || 'ট্রেন্ডিং নিউজ'}
             </h2>
           </div>
         )}
         
         {isLoading ? (
           <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pb-4">
               {[1,2,3].map(i => <div key={i} className="h-40 xl:h-48 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />)}
           </div>
         ) : (
            <div className={`grid grid-cols-${config.columnsMobile || 1} md:grid-cols-2 lg:grid-cols-${config.columnsDesktop || 3} gap-4 xl:gap-6`}>
               {posts.map(post => (
                 <NewsCard key={post.id} post={post} variant={"grid"} isDarkTheme={isDark} />
               ))}
            </div>
         )}
      </div>
    );
  }
  
  if (section.type === 'hero') {
    return <HeroSectionHero section={section} isDark={isDark} />;
  }
  
  if (section.type === 'newspaper' || config.layoutStyle === 'newspaper') {
    const mainCat = config.category || 'বাংলাদেশ';
    const sides = config.sideCategories || [];
    
    // Fetch main category
    const { data: mainData, isLoading: mainLoading } = useSWR(`dynamic_${mainCat}`, () => bloggerService.getPostsByLabel(mainCat, 5));
    const mainPosts = mainData?.items || [];
    
    // Default desktop columns
    const colsDesk = config.columnsDesktop || 4;

    return (
      <div className={`mb-0 w-full ${config.bgColor || ''} ${config.padding || 'py-[10px]'} rounded-xl`}>
         <div className="flex flex-col xl:flex-row gap-6 w-full">
            {/* Main Featured */}
            <div className={`w-full ${sides.length > 0 ? 'xl:w-[760px]' : ''} shrink-0`}>
               <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
                 <h2 className={`text-lg font-bold font-bengali ${isDark ? 'text-white' : 'text-[#111111] dark:text-white'} flex items-center`}>
                   <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{section.title || mainCat}
                 </h2>
                 <Link to={`/category/${encodeURIComponent(mainCat)}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700'>আরও</Link>
               </div>

                <div className='flex flex-col md:grid md:grid-cols-2 gap-6 w-full'>
                  {mainLoading ? (
                    <div className="h-[326px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg col-span-2"></div>
                  ) : (
                    <>
                      {mainPosts[0] && (
                        <div className='mb-3 md:mb-0 w-full h-[326px]'>
                          <NewsCard post={mainPosts[0]} featured={true} isDarkTheme={isDark} />
                        </div>
                      )}
                      <div className='flex flex-col w-full h-[326px] justify-between'>
                        {mainPosts.slice(1, 5).map((post, i) => (
                          <NewsCard key={post.id} post={post} className={i === 3 ? 'pb-0' : ''} isDarkTheme={isDark} />
                        ))}
                      </div>
                    </>
                  )}
                </div>
            </div>

            {/* Sub Categories */}
            {sides.length > 0 && (
              <div className={`grid grid-cols-1 ${sides.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-4 flex-1 h-full`}>
                 {sides.map((cat, idx) => {
                    if (cat.toLowerCase().startsWith('ad:')) {
                       const adSlot = cat.split(':')[1] || '';
                       return (
                          <div key={idx} className="w-full flex-1 h-full rounded-xl flex items-center justify-center p-2 mb-4 bg-gray-50 dark:bg-gray-800/50 overflow-hidden">
                             {adSlot ? <AdBanner dataAdSlot={adSlot.trim().replace(/[^0-9]/g, '')} /> : <div className="text-sm font-mono text-gray-500">AdSlot ID missing</div>}
                          </div>
                       );
                    }
                    return <SideCategoryWidget key={idx} category={cat} isDark={isDark} />;
                 })}
              </div>
            )}
         </div>
      </div>
    );
  }

  // Handling other dynamic layouts
  const cat = config.category || 'বাংলাদেশ';
  const postLimit = config.itemCount || (config.layoutStyle === 'split' ? 5 : 6);
  const { data, isLoading } = useSWR(`dynamic_${cat}`, () => bloggerService.getPostsByLabel(cat, postLimit));
  const posts = data?.items || [];
  
  const showTitle = config.layoutStyle !== 'grid_no_title' && config.showTitle !== false;
  const colsDesk = config.columnsDesktop || 4;
  const colsMob = config.columnsMobile || 2;

  const getContainerClasses = () => {
     if (config.layoutStyle === 'carousel') return "flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 scrollbar-hide";
     if (config.layoutStyle === 'list' || config.layoutStyle === 'list_no_image' || config.layoutStyle === 'timeline') {
       const listColsMd = { 1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4', 5: 'md:grid-cols-5' }[Math.max(1, colsDesk - 1)] || 'md:grid-cols-3';
        return `grid grid-cols-1 ${listColsMd} gap-4`;
     }
     if (config.layoutStyle === 'masonry') {
         const colMob = { 1: 'columns-1', 2: 'columns-2', 3: 'columns-3' }[colsMob] || 'columns-2';
         const colDesk = { 1: 'md:columns-1', 2: 'md:columns-2', 3: 'md:columns-3', 4: 'md:columns-4', 5: 'md:columns-5', 6: 'md:columns-6' }[colsDesk] || 'md:columns-4';
         return `${colMob} ${colDesk} gap-4 space-y-4`;
      }
     if (config.layoutStyle === 'split') return "flex flex-col md:flex-row gap-6";
     // Default grid
      const gridMob = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' }[colsMob] || 'grid-cols-2';
      const gridDesk = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5', 6: 'lg:grid-cols-6' }[colsDesk] || 'lg:grid-cols-4';
      return `grid ${gridMob} md:grid-cols-3 ${gridDesk} gap-4`;
  };

  const getVariant = () => {
    if (config.layoutStyle === 'list' || config.layoutStyle === 'timeline') return 'list';
    if (config.layoutStyle === 'list_no_image') return 'list_no_image';
    return 'grid';
  };

  return (
    <div className={`mb-0 w-full ${config.bgColor || ''} ${config.padding || 'py-[10px]'} rounded-xl`}>
       {showTitle && (
         <div className='flex items-center justify-between mb-0 pr-1.5 w-full'>
           <h2 className={`text-lg font-bold font-bengali flex items-center ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
             <span className='w-1 h-5 bg-[#D32F2F] rounded-full mr-2'></span>{section.title || cat}
           </h2>
           <Link to={`/category/${encodeURIComponent(cat)}`} className='text-[#D32F2F] text-sm font-bold font-bengali hover:text-red-700'>আরও</Link>
         </div>
       )}

       {isLoading ? (
          <div className="h-[200px] bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"></div>
       ) : (
          <div className={config.layoutStyle === 'split' ? "w-full" : getContainerClasses()}>
             {config.layoutStyle === 'split' ? (
                <div className="flex flex-col md:flex-row gap-6 w-full">
                   {posts[0] && (
                      <div className="w-full md:w-1/2 lg:w-3/5">
                        <NewsCard post={posts[0]} featured={true} isDarkTheme={isDark} />
                      </div>
                   )}
                   <div className="w-full md:w-1/2 lg:w-2/5 flex flex-col gap-4">
                      {posts.slice(1, 5).map(post => <NewsCard key={post.id} post={post} variant="list" isDarkTheme={isDark} />)}
                   </div>
                </div>
             ) : (
                posts.map((post, i) => (
                  <div key={post.id} className={config.layoutStyle === 'carousel' ? `w-[240px] md:w-[280px] shrink-0 snap-start` : config.layoutStyle === 'masonry' ? 'break-inside-avoid' : ''}>
                    <NewsCard post={post} variant={getVariant() as any} isDarkTheme={isDark} />
                  </div>
                ))
             )}
          </div>
       )}
    </div>
  );
}

function SideCategoryWidget({ category, isDark }: { category: string, isDark: boolean }) {
  const { data, isLoading } = useSWR(`side_${category}`, () => bloggerService.getPostsByLabel(category, 5));
  return (
     <WidgetPostList title={category} posts={data?.items || []} loading={isLoading} />
  );
}
